import React, { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { readRemScale } from '@/utils/remScale';
import { cx } from './cx';
import { topOverlayElement } from './overlayStack';
import { focusableIn } from './useFocusTrap';

export interface PopoverTriggerProps {
  ref: React.Ref<HTMLButtonElement>;
  'aria-haspopup': 'dialog';
  'aria-expanded': boolean;
  'aria-controls'?: string;
  onClick: () => void;
}

export interface PopoverProps {
  trigger: (props: PopoverTriggerProps) => React.ReactElement;
  /** Accessible name of the panel (translated). */
  'aria-label': string;
  align?: 'start' | 'end';
  /** Panel width in px at scale 1 (it grows with the UI scale); defaults to 320, capped to the viewport. */
  width?: number;
  className?: string;
  /** Scroll the panel when its content is taller than the space on screen. */
  scrollable?: boolean;
  /** Called when the panel opens or closes (e.g. to load content on open). */
  onOpenChange?: (open: boolean) => void;
  children: React.ReactNode | ((close: () => void) => React.ReactNode);
}

/** Anchored, non-modal panel (notifications, help): Escape and outside click close it. */
export default function Popover({ trigger, 'aria-label': ariaLabel, align = 'end', width = 320, className, scrollable = false, onOpenChange, children }: PopoverProps) {
  const id = useId();
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top?: number; bottom?: number; maxHeight?: number; left?: number; right?: number; width: number }>({ top: 0, width });
  const [target, setTarget] = useState<HTMLElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  const place = useCallback(() => {
    const r = triggerRef.current?.getBoundingClientRect();
    if (!r) return;
    const vw = document.documentElement.clientWidth || window.innerWidth;
    const vh = document.documentElement.clientHeight || window.innerHeight;
    // Sizes follow the rem scale (like the CSS); the 6/8/16px offsets from the trigger and edges stay px.
    const k = readRemScale();
    const edge = 8;
    const w = Math.min(width * k, vw - edge * 2);
    // Open upwards when there is little room below and more above (e.g. a picker in a phone bottom sheet).
    const below = vh - r.bottom - 6 - edge;
    const above = r.top - 6 - edge;
    const up = below < 240 * k && above > below;
    const cap = Math.min(480 * k, vh * 0.7);
    const vertical = up ? { bottom: vh - r.top + 6, maxHeight: Math.min(cap, above) } : { top: r.bottom + 6, maxHeight: Math.min(cap, below) };
    // End alignment hugs the trigger's right edge, then slides right if that would leave the screen.
    const right = Math.min(Math.max(edge, vw - r.right), vw - w - edge);
    setPos(
      align === 'end'
        ? { ...vertical, right, width: w }
        : { ...vertical, left: Math.max(edge, Math.min(r.left, vw - w - edge)), width: w }
    );
  }, [align, width]);

  const onOpenChangeRef = useRef(onOpenChange);
  onOpenChangeRef.current = onOpenChange;
  useEffect(() => {
    onOpenChangeRef.current?.(open);
  }, [open]);

  const close = useCallback((refocus: boolean) => {
    setOpen(false);
    if (refocus) triggerRef.current?.focus();
  }, []);

  useLayoutEffect(() => {
    if (!open || !panelRef.current) return;
    (focusableIn(panelRef.current)[0] ?? panelRef.current).focus({ preventScroll: true });
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (triggerRef.current?.contains(t) || panelRef.current?.contains(t)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    window.addEventListener('resize', place);
    // Follow the trigger when a scroll container (page, dialog body) moves it.
    window.addEventListener('scroll', place, true);
    return () => {
      document.removeEventListener('mousedown', onDown);
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [open, place]);

  return (
    <>
      {trigger({
        ref: triggerRef,
        'aria-haspopup': 'dialog',
        'aria-expanded': open,
        'aria-controls': open ? id : undefined,
        onClick: () => {
          if (open) return close(false);
          place();
          setTarget(topOverlayElement() ?? document.body);
          setOpen(true);
        },
      })}
      {open
        ? createPortal(
            <div
              ref={panelRef}
              id={id}
              role="dialog"
              aria-label={ariaLabel}
              tabIndex={-1}
              onKeyDown={(e) => {
                if (e.key === 'Escape') {
                  e.preventDefault();
                  close(true);
                }
              }}
              style={{ top: pos.top, bottom: pos.bottom, maxHeight: pos.maxHeight, left: pos.left, right: pos.right, width: pos.width }}
              className={cx(
                'fixed z-popover flex max-h-[min(70dvh,30rem)] flex-col rounded-container border k-panel bg-raised text-primary focus:outline-none',
                scrollable ? 'overflow-y-auto overscroll-contain cosmos-scroll-y' : 'overflow-hidden',
                className
              )}
            >
              {typeof children === 'function' ? children(() => close(true)) : children}
            </div>,
            target ?? document.body
          )
        : null}
    </>
  );
}
